/*
 * Sample onboarding projects (P5.5–P5.10). A project takes a won customer
 * from registration to handover to Customer Success. Its checklist comes
 * from the onboarding template of the package sold (Settings → Onboarding
 * Templates), and it may only close when the close rule is met:
 * checklist complete + go-live confirmed + critical issues cleared or
 * accepted + customer sign-off on file.
 */
import { daysFromNow } from "@/helpers/crm";

// The onboarding path. Like every stage list, a company may rename or add steps; `kind` is what the system relies on.
export const onboardingStages = [
  { key: "registration", name: "Registration", kind: "open", order: 1 },
  { key: "contract", name: "Contract", kind: "open", order: 2 },
  { key: "subscription", name: "Subscription", kind: "open", order: 3 },
  { key: "kickoff", name: "Kick-off", kind: "open", order: 4 },
  { key: "configuration", name: "Configuration", kind: "open", order: 5 },
  { key: "data_migration", name: "Data migration", kind: "open", order: 6 },
  { key: "training", name: "Training", kind: "open", order: 7 },
  { key: "uat", name: "UAT", kind: "open", order: 8 },
  { key: "go_live", name: "Go-live", kind: "open", order: 9 },
  { key: "signoff", name: "Sign-off", kind: "open", order: 10 },
  { key: "handover", name: "Handover", kind: "won", order: 11 },
  { key: "on_hold", name: "On hold", kind: "paused", order: 90 },
  { key: "cancelled", name: "Cancelled", kind: "lost", order: 99 },
];
const ORDER = Object.fromEntries(onboardingStages.map((s) => [s.key, s.order]));

/*
 * Onboarding templates — a new master for Settings → Onboarding Templates.
 * Each package points at one; `mandatory` items block the close rule.
 */
export const onboardingTemplates = [
  {
    id: 1,
    name: "DeeHr360 HRM — new tenant",
    product_id: 1,
    package_ids: [1, 2, 3],
    target_days: 45,
    items: [
      { key: "profile", stage: "registration", label: "Customer profile, branches and users captured", mandatory: true },
      { key: "contacts", stage: "registration", label: "Primary, decision-maker and technical contacts registered", mandatory: true },
      { key: "contract", stage: "contract", label: "Signed contract and PO on file", mandatory: true },
      { key: "subscription", stage: "subscription", label: "Subscription, licences and renewal date set", mandatory: true },
      { key: "provision", stage: "subscription", label: "Provision tenant on the DeeHr360 platform (tenant code + sign-in address)", mandatory: true },
      { key: "account_link", stage: "subscription", label: "Record the HRM tenant code and sign-in address as the customer's product account", mandatory: true },
      { key: "support_token", stage: "configuration", label: "Issue support token and configure it in the client's DeeHr360 settings (Test connection passes)", mandatory: true },
      { key: "kickoff", stage: "kickoff", label: "Kick-off held, scope and plan agreed with customer", mandatory: true },
      { key: "structure", stage: "configuration", label: "Company structure, pay elements and statutory rates configured", mandatory: true },
      { key: "integrations", stage: "configuration", label: "Bank file and KRA iTax exports set up", mandatory: false },
      { key: "employees", stage: "data_migration", label: "Employee master data imported and approved", mandatory: true },
      { key: "ytd", stage: "data_migration", label: "Year-to-date payroll and leave balances loaded", mandatory: true },
      { key: "admin_training", stage: "training", label: "System administrators trained", mandatory: true },
      { key: "user_training", stage: "training", label: "Employee self-service training delivered", mandatory: false },
      { key: "uat", stage: "uat", label: "Parallel payroll run passed and UAT signed", mandatory: true },
      { key: "readiness", stage: "go_live", label: "Go-live readiness check passed", mandatory: true },
      { key: "first_run", stage: "go_live", label: "First live payroll processed", mandatory: true },
      { key: "signoff", stage: "signoff", label: "Customer sign-off received", mandatory: true },
      { key: "handover_pack", stage: "handover", label: "Handover pack shared with the CSM", mandatory: true },
    ],
  },
  {
    id: 2,
    name: "Demulla LMS — new tenant",
    product_id: 2,
    package_ids: [4, 5, 6],
    target_days: 50,
    items: [
      { key: "profile", stage: "registration", label: "Customer profile, branches and loan officers captured", mandatory: true },
      { key: "contacts", stage: "registration", label: "Primary, decision-maker and technical contacts registered", mandatory: true },
      { key: "contract", stage: "contract", label: "Signed contract, PO and data-processing agreement on file", mandatory: true },
      { key: "subscription", stage: "subscription", label: "Subscription, loan-book band and renewal date set", mandatory: true },
      { key: "provision", stage: "subscription", label: "Provision tenant on the LMS platform (tenant code + sign-in address)", mandatory: true },
      { key: "account_link", stage: "subscription", label: "Record the LMS tenant code and sign-in address as the customer's product account", mandatory: true },
      { key: "kickoff", stage: "kickoff", label: "Kick-off held, scope and plan agreed with customer", mandatory: true },
      { key: "loan_products", stage: "configuration", label: "Configure loan products & interest rules (rates, fees, penalties, grace periods)", mandatory: true },
      { key: "mpesa", stage: "configuration", label: "M-Pesa (paybill / STK / B2C) and bank integration live in test", mandatory: true },
      { key: "support_token", stage: "configuration", label: "Issue support token and configure it in the client's LMS tenant settings (Test connection passes)", mandatory: true },
      { key: "borrowers", stage: "data_migration", label: "Borrower records and KYC imported and approved", mandatory: true },
      { key: "loan_book", stage: "data_migration", label: "Open loan book, balances and arrears loaded and reconciled", mandatory: true },
      { key: "admin_training", stage: "training", label: "Credit, collections and finance staff trained", mandatory: true },
      { key: "uat", stage: "uat", label: "UAT: application → appraisal → disbursement → repayment → penalty cycle passed", mandatory: true },
      { key: "readiness", stage: "go_live", label: "Go-live readiness check passed", mandatory: true },
      { key: "first_day", stage: "go_live", label: "First live disbursement and M-Pesa repayment posted", mandatory: true },
      { key: "signoff", stage: "signoff", label: "Customer sign-off received", mandatory: true },
      { key: "handover_pack", stage: "handover", label: "Handover pack shared with the CSM", mandatory: true },
    ],
  },
  {
    id: 3,
    name: "Standalone application — support onboarding",
    product_id: null,
    package_ids: [9, 10],
    target_days: 20,
    items: [
      { key: "profile", stage: "registration", label: "Customer profile and support contacts captured", mandatory: true },
      { key: "contract", stage: "contract", label: "Support & maintenance contract on file", mandatory: true },
      { key: "instance", stage: "subscription", label: "Register the installation as product account (address, version, environment)", mandatory: true },
      { key: "support_token", stage: "configuration", label: "Issue support token and configure it in the installation's settings (SUPPORT_URL + SUPPORT_TOKEN; Test connection passes)", mandatory: true },
      { key: "webhook", stage: "configuration", label: "Webhook endpoint verified (signed test delivery returns 2xx)", mandatory: true },
      { key: "runbook", stage: "training", label: "Runbook, access and escalation contacts handed to the Service Desk", mandatory: true },
      { key: "signoff", stage: "signoff", label: "Customer sign-off received", mandatory: true },
      { key: "handover_pack", stage: "handover", label: "Handover pack shared with the CSM", mandatory: true },
    ],
  },
  // Enterprise Accounting is still being built; its template is a placeholder until the product is on sale.
  { id: 4, name: "Enterprise Accounting — new tenant (draft)", product_id: 3, package_ids: [7, 8], target_days: 40, items: [] },
];
export const templateFor = (packageId) => onboardingTemplates.find((t) => t.package_ids.includes(packageId));

// Everything before the current stage is done; the current stage partly.
const doneFor = (template, stage, extraDone = [], notDone = []) => {
  const done = {};
  template.items.forEach((it) => {
    done[it.key] = (stage === "handover" || ORDER[it.stage] < ORDER[stage] || extraDone.includes(it.key)) && !notDone.includes(it.key);
  });
  return done;
};

const milestonesFor = (p) => {
  const M = (name, stage, owner_id, due, depends_on = null) => {
    const reached = ORDER[stage] < ORDER[p.stage] || p.stage === "handover" || (stage === "go_live" && !!p.actual_go_live);
    return {
      name, stage, owner_id, due: daysFromNow(due), depends_on,
      status: reached ? "done" : ORDER[stage] === ORDER[p.stage] ? (due < 0 ? "overdue" : "in_progress") : "not_started",
      done_on: reached ? daysFromNow(due - 1) : null,
    };
  };
  const s = p.started_offset;
  const g = p.planned_offset;
  const span = g - s;
  const at = (f) => Math.round(s + span * f);
  return [
    M("Kick-off meeting", "kickoff", 6, at(0.08)),
    M("System configured", "configuration", 6, at(0.3), "Kick-off meeting"),
    M("Data migrated and approved", "data_migration", 6, at(0.5), "System configured"),
    M("Training complete", "training", 7, at(0.7), "System configured"),
    M("UAT signed", "uat", 6, at(0.85), "Data migrated and approved"),
    M("Go-live", "go_live", 6, g, "UAT signed"),
    M("Customer sign-off", "signoff", p.csm_id, g + 7, "Go-live"),
    M("Handover to Customer Success", "handover", p.csm_id, g + 14, "Customer sign-off"),
  ];
};

const P = (id, customer_id, package_ids, stage, started, planned, forecast, extra = {}) => {
  const template = templateFor(package_ids[0]);
  const p = {
    id,
    code: `ONB-${String(id + 30).padStart(4, "0")}`,
    customer_id,
    package_ids,
    template_id: template.id,
    title: extra.title ?? "Initial implementation",
    implementation_owner_id: 6,
    csm_id: extra.csm_id ?? 7,
    stage,
    started_on: daysFromNow(started),
    started_offset: started,
    planned_offset: planned,
    planned_go_live: daysFromNow(planned),
    forecast_go_live: forecast === null ? null : daysFromNow(forecast),
    actual_go_live: null,
    risk: "normal",
    health_score: 80,
    readiness: 70,
    progress: 0,
    issues: [],
    signoff: null,
    handover: null,
    failed: null,
    ...extra,
  };
  p.checklist = doneFor(template, stage, extra.extra_done, extra.not_done);
  const items = template.items;
  p.progress = extra.progress ?? Math.round((items.filter((i) => p.checklist[i.key]).length / items.length) * 100);
  p.milestones = milestonesFor(p);
  return p;
};

export const onboardingProjects = [
  // Rift Valley Millers: big rollout, data migration is the bottleneck.
  P(1, 8, [3], "data_migration", -25, 35, 42, {
    csm_id: 10, risk: "at_risk", health_score: 64, readiness: 52,
    risk_note: "Employee file from the customer failed validation twice; go-live forecast slipped 7 days.",
    extra_done: [], not_done: ["integrations"],
    issues: [
      { id: "ISS-118", title: "Employee file: 37 rows missing KRA PIN", severity: "high", status: "open", owner: "Naomi Jepkosgei (customer)", raised: daysFromNow(-4) },
      { id: "ISS-121", title: "Bank file format for Equity Bank not confirmed", severity: "medium", status: "open", owner: "Kevin Mwangi", raised: daysFromNow(-2) },
    ],
  }),
  // Mwangaza Academy: live since yesterday, one critical issue blocks sign-off.
  P(2, 7, [1], "go_live", -40, -3, -1, {
    risk: "at_risk", health_score: 58, readiness: 88, actual_go_live: daysFromNow(-1),
    risk_note: "Went live two days late; NSSF (new rates) calculation issue open.",
    extra_done: ["readiness", "first_run"], not_done: ["user_training"],
    issues: [
      { id: "ISS-109", title: "NSSF tier II deduction wrong for staff above upper limit", severity: "critical", status: "open", owner: "Kevin Mwangi", raised: daysFromNow(-1) },
      { id: "ISS-104", title: "Payslip logo blurred on PDF", severity: "low", status: "accepted", owner: "Kevin Mwangi", raised: daysFromNow(-6), note: "Accepted by the principal; fix in next release." },
      { id: "ISS-101", title: "Leave balances off by 1 day for 4 staff", severity: "medium", status: "resolved", owner: "Collins Mutai (customer)", raised: daysFromNow(-9) },
    ],
  }),
  // Kampala Retail Hub: on track; tenant still provisioning while Uganda statutory tables are confirmed.
  P(3, 11, [2], "configuration", -15, 45, 45, {
    health_score: 82, readiness: 40, extra_done: ["structure"], not_done: ["provision", "account_link", "support_token"],
    issues: [{ id: "ISS-125", title: "Uganda PAYE and NSSF (UG) tables — confirm the rates pack before provisioning the tenant", severity: "high", status: "open", owner: "Kevin Mwangi", raised: daysFromNow(-3) }],
  }),
  // Pwani Logistics: expansion to two new branches.
  P(4, 18, [3], "training", -30, 14, 14, { title: "Mombasa & Malindi branch rollout", csm_id: 10, health_score: 86, readiness: 74 }),
  // Gelisa: an LMS client adding DeeHr360 for its own staff — a second product account on the same customer.
  P(5, 19, [1], "kickoff", -5, 55, 55, { title: "DeeHr360 HR & Payroll add-on", csm_id: 7, health_score: 78, readiness: 15, extra_done: ["provision"], not_done: ["account_link", "contract"] }),
  // Gatsby Insurance: closed and handed over.
  P(6, 13, [2], "handover", -150, -95, -92, {
    health_score: 91, readiness: 100, actual_go_live: daysFromNow(-92), progress: 100,
    signoff: { by: "Henry Mugo", title: "CFO", on: daysFromNow(-85), file: "Gatsby go-live sign-off.pdf" },
    handover: { csm_id: 7, on: daysFromNow(-80), by: "Kevin Mwangi" },
  }),
  // Smart Duka Chain: closed and handed over, late.
  P(7, 10, [2], "handover", -220, -170, -160, {
    csm_id: 10, health_score: 74, readiness: 100, actual_go_live: daysFromNow(-160), progress: 100,
    signoff: { by: "Kevin Langat", title: "Owner", on: daysFromNow(-150), file: "Smart Duka sign-off.pdf" },
    handover: { csm_id: 10, on: daysFromNow(-148), by: "Kevin Mwangi" },
  }),
  // Kijani Solar: LMS pilot for its pay-as-you-go solar loans, cancelled during data migration.
  P(8, 16, [4], "cancelled", -110, -60, null, {
    title: "LMS pilot — PAYG solar loans", csm_id: 10, risk: "critical", health_score: 30, readiness: 35, progress: 41,
    failed: { at: "data_migration", code: "SRV-02", note: "Loan-book export from their PAYG platform never reconciled; customer stopped the pilot and kept its in-house system. LMS tenant LMS005 archived.", on: daysFromNow(-55), by: "Kevin Mwangi" },
  }),
  // Mkopo Bora Credit: a new LMS client being onboarded as tenant LMS004 on the LMS platform.
  P(9, 20, [4], "configuration", -12, 38, 42, {
    title: "New LMS tenant — Mkopo Bora Credit", csm_id: 10, health_score: 76, readiness: 45, risk: "at_risk",
    risk_note: "Safaricom has not yet approved the paybill C2B URLs; go-live forecast +4 days.",
    extra_done: ["loan_products", "support_token"], not_done: ["mpesa"],
    issues: [
      { id: "ISS-131", title: "Paybill 522 714: C2B validation/confirmation URLs awaiting Safaricom approval", severity: "high", status: "open", owner: "Kevin Mwangi", raised: daysFromNow(-3) },
      { id: "ISS-132", title: "Penalty rule: confirm 5% flat vs 0.5% daily on arrears", severity: "medium", status: "open", owner: "Samuel Cheruiyot (customer)", raised: daysFromNow(-2) },
    ],
  }),
  // Gelisa: original LMS onboarding, closed and handed over.
  P(10, 19, [4, 6], "handover", -540, -480, -476, {
    title: "New LMS tenant — Gelisa", health_score: 88, readiness: 100, actual_go_live: daysFromNow(-476), progress: 100,
    signoff: { by: "Joyce Wanjala", title: "CEO", on: daysFromNow(-470), file: "Gelisa LMS go-live sign-off.pdf" },
    handover: { csm_id: 7, on: daysFromNow(-465), by: "Kevin Mwangi" },
  }),
  // Kilimo Fresh: bespoke Export Tracker brought under support — a standalone application.
  P(11, 21, [9], "handover", -300, -285, -284, {
    title: "Export Tracker — support onboarding", health_score: 90, readiness: 100, actual_go_live: daysFromNow(-284), progress: 100,
    signoff: { by: "Mary Atieno", title: "Finance Manager", on: daysFromNow(-282), file: "Kilimo Fresh support acceptance.pdf" },
    handover: { csm_id: 7, on: daysFromNow(-280), by: "Kevin Mwangi" },
  }),
];
// The cancelled project's checklist stops where it fell out.
const cancelled = onboardingProjects.find((p) => p.stage === "cancelled");
cancelled.checklist = doneFor(templateFor(cancelled.package_ids[0]), "data_migration");
cancelled.milestones = milestonesFor({ ...cancelled, stage: "data_migration" });

export const onboardingById = (id) => onboardingProjects.find((p) => p.id === Number(id));
export const onboardingFor = (customerId) => onboardingProjects.filter((p) => p.customer_id === Number(customerId));
export const onboardingStage = (key) => onboardingStages.find((s) => s.key === key);

/*
 * Phase 2 detail for the projects that are mid-flight. Keyed by project id;
 * screens fall back to "not started" when a project has none.
 */
export const migrationFor = {
  1: {
    requirements: [
      { entity: "Employees", fields: "Staff no, name, ID no, KRA PIN, NSSF, SHIF, bank, branch, grade, basic pay", mandatory: true, template: "Employee import v3.xlsx" },
      { entity: "Year-to-date payroll", fields: "Staff no, month, gross, PAYE, NSSF, SHIF, housing levy", mandatory: true, template: "YTD payroll.xlsx" },
      { entity: "Leave balances", fields: "Staff no, leave type, balance as at cut-over", mandatory: true, template: "Leave balances.xlsx" },
      { entity: "Bank & branch codes", fields: "Staff no, bank, branch code, account no", mandatory: true, template: "Bank details.xlsx" },
    ],
    submissions: [
      { version: 3, entity: "Employees", file: "RVM employees v3.xlsx", rows: 812, at: daysFromNow(-1), by: "Naomi Jepkosgei", status: "validating" },
      { version: 2, entity: "Employees", file: "RVM employees v2.xlsx", rows: 806, at: daysFromNow(-5), by: "Naomi Jepkosgei", status: "rejected" },
      { version: 1, entity: "Employees", file: "RVM staff list.xlsx", rows: 790, at: daysFromNow(-11), by: "Naomi Jepkosgei", status: "rejected" },
      { version: 1, entity: "Bank & branch codes", file: "RVM bank details.xlsx", rows: 798, at: daysFromNow(-6), by: "Eunice Wangui", status: "approved" },
    ],
    validation: {
      file: "RVM employees v2.xlsx", rows: 806, ok: 742,
      checks: [
        { check: "Missing mandatory fields", count: 37, detail: "KRA PIN empty (31), bank account empty (6)" },
        { check: "Duplicates", count: 9, detail: "Same ID number on two staff numbers" },
        { check: "Format errors", count: 18, detail: "Dates as text (12), phone without country code (6)" },
      ],
    },
    approvals: [{ entity: "Bank & branch codes", by: "Eunice Wangui (customer)", at: daysFromNow(-4), note: "Checked against the September bank file" }],
  },
};

export const trainingFor = {
  1: [
    { title: "Payroll administrators", date: daysFromNow(9), trainer: "Kevin Mwangi", mode: "On-site, Nakuru", invited: 6, attended: null, status: "scheduled" },
    { title: "HR managers: appraisals & leave approvals", date: daysFromNow(12), trainer: "Grace Njeri", mode: "Online", invited: 8, attended: null, status: "scheduled" },
  ],
  2: [
    { title: "School administrators", date: daysFromNow(-12), trainer: "Kevin Mwangi", mode: "On-site, Thika", invited: 4, attended: 4, status: "done" },
    { title: "Teachers: self-service & leave", date: daysFromNow(-8), trainer: "Grace Njeri", mode: "Online", invited: 38, attended: 21, status: "done" },
  ],
  4: [
    { title: "Mombasa branch HR officers", date: daysFromNow(-2), trainer: "Kevin Mwangi", mode: "On-site, Mombasa", invited: 9, attended: 8, status: "done" },
    { title: "Malindi branch HR officers", date: daysFromNow(3), trainer: "Kevin Mwangi", mode: "On-site, Malindi", invited: 7, attended: null, status: "scheduled" },
  ],
};

trainingFor[9] = [
  { title: "Credit & collections officers", date: daysFromNow(8), trainer: "Kevin Mwangi", mode: "On-site, Nakuru", invited: 6, attended: null, status: "scheduled" },
];

export const uatFor = {
  2: {
    cases: [
      { id: "TC-01", title: "Run October payroll for 52 staff", module: "Payroll", result: "pass", retest: null },
      { id: "TC-02", title: "PAYE matches iTax calculator", module: "Statutory", result: "pass", retest: null },
      { id: "TC-03", title: "NSSF tier I & II deductions", module: "Statutory", result: "fail", retest: "pass" },
      { id: "TC-04", title: "Apply and approve annual leave", module: "Leave", result: "pass", retest: null },
      { id: "TC-05", title: "Bank file for KCB", module: "Payments", result: "pass", retest: null },
      { id: "TC-06", title: "Payslips emailed to staff", module: "Self-service", result: "fail", retest: "pass" },
    ],
    defects: [
      { id: "DEF-31", title: "NSSF tier II not capped", severity: "critical", status: "fixed", retest: "passed" },
      { id: "DEF-32", title: "Payslip email bounced for staff with no email", severity: "medium", status: "fixed", retest: "passed" },
    ],
    signoff: { by: "Sr. Margaret Akoth", on: daysFromNow(-5), note: "Signed subject to NSSF fix holding in first live run." },
  },
};
